import React, { useContext, useMemo, useState } from 'react'
import { barBox, rowCenterY } from '../lib/geometry'
import { routeLink, type Endpoint, type LinkPath } from '../lib/link-routing'
import { visibleRowOf, type Tree } from '../lib/tree'
import { useGanttStore, useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity, ScheduleDependency, ScheduleExternalActivity } from '../types'
import { GanttHoverDispatchContext } from './gantt-hover'
import { useGanttLayout } from './gantt-layout'
import DependencyLink from './timeline/DependencyLink'

interface Props {
  dependencies: ScheduleDependency[]
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  tree: Tree<ScheduleActivity>
  collapsedIds: Set<string>
  showCriticalPath: boolean
  /** First / last visible row index currently rendered by the virtualizer (overscan already applied). */
  visibleFrom: number
  visibleTo: number
}

interface LinkEntry { dependency: ScheduleDependency; path: LinkPath; critical: boolean }

/**
 * Draws every dependency line between the bars currently on screen (spec B §8.5),
 * including a line to a cross-schedule ghost (§8.6 — `GanttTimeline` draws the
 * ghost marker itself, on the local endpoint's row).
 *
 * Reads only the hover *dispatcher*, never the hover value: this layer draws
 * every link, so subscribing to the value would re-render it on every pointer
 * move (the same problem the Task 9 review found in `GanttTimeline`). The
 * "is this particular link hovered" highlight is local component state instead.
 */
const DependencyLayer: React.FC<Props> = ({ dependencies, byId, externalById, tree, collapsedIds, showCriticalPath, visibleFrom, visibleTo }) => {
  const { rowIndexById, range, pxPerDay } = useGanttLayout()
  const selectedLinkId = useGanttStore((s) => s.selectedLinkId)
  const api = useGanttStoreApi()
  const setHover = useContext(GanttHoverDispatchContext)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const links = useMemo(() => {
    interface RowLookup { idx: number; activityId: string; local: boolean }

    /**
     * Review fix (Important #2): split into a cheap row-index lookup (`Map` reads only) and the
     * actual bar geometry (`barBox`, which parses ISO dates). Every dependency used to pay for
     * `barBox` on both endpoints *before* the visibility check below discarded it — with ~4 900
     * links that was tens of thousands of date parses per virtual-window shift during a scroll.
     * Now the cheap lookup runs for every link, but `resolveEndpoint` (the expensive part) only
     * runs for the links that survive the `[visibleFrom, visibleTo]` filter.
     */
    const resolveRow = (activityId: string, rowFor: string): RowLookup | null => {
      const local = byId.get(activityId)
      if (local) {
        const visId = visibleRowOf(tree, collapsedIds, activityId)
        const idx = rowIndexById.get(visId)
        return idx == null ? null : { idx, activityId: visId, local: true }
      }
      if (!externalById.has(activityId)) return null
      const idx = rowIndexById.get(visibleRowOf(tree, collapsedIds, rowFor))
      return idx == null ? null : { idx, activityId, local: false }
    }

    const resolveEndpoint = (row: RowLookup): Endpoint => {
      const a = row.local ? byId.get(row.activityId)! : externalById.get(row.activityId)!
      const box = barBox(a, range.start, pxPerDay)
      return { x1: box.x, x2: box.x + Math.max(2, box.width), y: rowCenterY(row.idx) }
    }

    const out: LinkEntry[] = []
    dependencies.forEach((d) => {
      const predLocal = byId.has(d.predecessorId)
      const succLocal = byId.has(d.successorId)
      if (!predLocal && !succLocal) return // neither end belongs to this schedule
      const rowFor = succLocal ? d.successorId : d.predecessorId // the local endpoint hosts the ghost
      const fromRow = resolveRow(d.predecessorId, rowFor)
      const toRow = resolveRow(d.successorId, rowFor)
      if (!fromRow || !toRow) return
      if (Math.max(fromRow.idx, toRow.idx) < visibleFrom || Math.min(fromRow.idx, toRow.idx) > visibleTo) return
      const from = resolveEndpoint(fromRow)
      const to = resolveEndpoint(toRow)
      const critical = showCriticalPath && predLocal && succLocal && !!byId.get(d.predecessorId)?.isCritical && !!byId.get(d.successorId)?.isCritical
      out.push({ dependency: d, path: routeLink(d.type, from, to), critical })
    })
    return out
  }, [dependencies, byId, externalById, tree, collapsedIds, rowIndexById, range.start, pxPerDay, showCriticalPath, visibleFrom, visibleTo])

  return (
    <g data-dependency-layer>
      {links.map(({ dependency, path, critical }) => (
        <DependencyLink
          key={dependency.id}
          path={path}
          critical={critical}
          selected={selectedLinkId === dependency.id}
          hovered={hoveredId === dependency.id}
          onClick={(e) => { e.stopPropagation(); api.getState().setSelectedLink(dependency.id) }}
          onPointerEnter={(e) => { setHoveredId(dependency.id); setHover({ kind: 'link', id: dependency.id, x: e.clientX, y: e.clientY }) }}
          onPointerLeave={() => { setHoveredId(null); setHover(null) }}
        />
      ))}
    </g>
  )
}

// Memoised for the same reason as `GanttTimeline`: this component only ever writes to
// the hover dispatcher, never reads the hover value, so it re-renders solely from its
// own local `hoveredId` state or an actual prop change (e.g. `selectedLinkId`, scroll).
export default React.memo(DependencyLayer)
